/**
 * PAPPO — AlertCard Component
 * Card de alerta de IA + AÇÕES sensíveis à categoria:
 *  - risco vindo de um contato (aliciamento/sexual/golpe/violência/bullying):
 *    Ver conversa · Cortar contato · Denunciar (se adulto) · Resolver · Descartar.
 *  - automutilação: NÃO se bloqueia/denuncia — faixa de acolhimento (CVV 188)
 *    + Ver conversa · Resolver · Descartar.
 */

import type { Alert } from '../../types/alert';
import { RISK_LABELS } from '../../types/alert';
import { AlertBadge } from './AlertBadge';
import { Avatar } from '../common/Avatar';
import {
  Clock, Eye, XCircle, AlertTriangle, CheckCircle2,
  MessageSquare, Scissors, ShieldAlert, LifeBuoy,
} from 'lucide-react';
import styles from './AlertCard.module.css';

interface AlertCardProps {
  alert: Alert;
  onClick?: (alert: Alert) => void;
  onDismiss?: (alert: Alert) => void;
  onResolve?: (alert: Alert) => void;
  onDenounce?: (alert: Alert) => void;
  onCutContact?: (alert: Alert) => void;
  onViewConversation?: (alert: Alert) => void;
}

function formatRelative(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m atrás`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h atrás`;
  const days = Math.floor(hours / 24);
  return `${days}d atrás`;
}

const STATUS_ICON = {
  pending: <AlertTriangle size={14} />,
  seen: <Eye size={14} />,
  dismissed: <XCircle size={14} />,
  resolved: <CheckCircle2 size={14} />,
} as const;

const STATUS_LABEL = {
  pending: 'Pendente',
  seen: 'Visto',
  dismissed: 'Descartado',
  resolved: 'Resolvido',
} as const;

export function AlertCard({
  alert, onClick, onDismiss, onResolve, onDenounce, onCutContact, onViewConversation,
}: AlertCardProps) {
  const classification = alert.classification;
  const category = classification?.risk_category ?? 'safe';
  const level = classification?.risk_level ?? 1;
  const justification = classification?.justification ?? '';

  const actionable = alert.status === 'pending' || alert.status === 'seen';
  const isSelfHarm = category === 'self_harm';
  const contact = alert.contact ?? null;
  const contactIsAdult = !!contact && contact.role !== 'child';

  const stop = (fn?: (a: Alert) => void) => (e: React.MouseEvent) => {
    e.stopPropagation();
    fn?.(alert);
  };

  return (
    <article
      className={`${styles.card} ${styles[alert.status]}`}
      onClick={() => onClick?.(alert)}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && onClick) onClick(alert);
      }}
      aria-label={`Alerta de ${RISK_LABELS[category]} para ${alert.child_name}`}
    >
      <div className={styles.accent} />

      <div className={styles.body}>
        <div className={styles.topRow}>
          <div className={styles.childInfo}>
            <Avatar name={alert.child_name ?? 'Criança'} size="sm" />
            <span className={styles.childName}>{alert.child_name}</span>
            {contact && (
              <span className={styles.withContact}>
                com <strong>{contact.name}</strong>
              </span>
            )}
          </div>
          <AlertBadge category={category} level={level} size="sm" />
        </div>

        {justification && <p className={styles.justification}>{justification}</p>}

        {alert.message_content && (
          <blockquote className={styles.messageQuote}>
            “{alert.message_content}”
          </blockquote>
        )}

        {/* Faixa de acolhimento — só automutilação */}
        {isSelfHarm && (
          <div className={styles.supportBanner}>
            <LifeBuoy size={18} />
            <div>
              <strong>Isto pede acolhimento, não punição.</strong> Converse com
              calma com {alert.child_name}. Apoio emocional 24h, gratuito e
              sigiloso: <a href="tel:188" onClick={(e) => e.stopPropagation()}>CVV 188</a>.
            </div>
          </div>
        )}

        <div className={styles.bottomRow}>
          <span className={`${styles.status} ${styles[`status_${alert.status}`]}`}>
            {STATUS_ICON[alert.status]}
            {STATUS_LABEL[alert.status]}
          </span>
          <span className={styles.time}>
            <Clock size={12} />
            {formatRelative(alert.created_at)}
          </span>
        </div>

        {/* Ações do responsável */}
        {actionable && (
          <div className={styles.actions}>
            {onViewConversation && alert.conversation_id && (
              <button type="button" className={styles.actBtn} onClick={stop(onViewConversation)}>
                <MessageSquare size={14} /> Ver conversa
              </button>
            )}
            {!isSelfHarm && onCutContact && contact && (
              <button type="button" className={styles.actBtn} onClick={stop(onCutContact)}>
                <Scissors size={14} /> Cortar contato
              </button>
            )}
            {!isSelfHarm && onDenounce && contactIsAdult && (
              <button type="button" className={`${styles.actBtn} ${styles.danger}`} onClick={stop(onDenounce)}>
                <ShieldAlert size={14} /> Denunciar
              </button>
            )}
            {onResolve && (
              <button type="button" className={`${styles.actBtn} ${styles.resolve}`} onClick={stop(onResolve)}>
                <CheckCircle2 size={14} /> Resolver
              </button>
            )}
            {onDismiss && (
              <button type="button" className={styles.dismissBtn} onClick={stop(onDismiss)}>
                <XCircle size={12} /> Descartar
              </button>
            )}
          </div>
        )}
      </div>
    </article>
  );
}
